import skillData from "../data/skills.json";

export type SkillTree = "growth" | "power" | "economy";

export interface SkillDef {
  id: string;
  tree: SkillTree;
  /** Max level. */
  max: number;
  /** Effect magnitude per level (interpretation depends on the skill). */
  per: number;
  /** Base stardust cost; each level costs `cost * (currentLevel + 1)`. */
  cost: number;
  icon: string;
}

export const SKILLS: readonly SkillDef[] = skillData as SkillDef[];
export const TREES: SkillTree[] = ["growth", "power", "economy"];

const BY_ID = new Map<string, SkillDef>(SKILLS.map((s) => [s.id, s]));

export function getSkill(id: string): SkillDef | undefined {
  return BY_ID.get(id);
}

export function skillsByTree(tree: SkillTree): SkillDef[] {
  return SKILLS.filter((s) => s.tree === tree);
}

/** Stardust cost to raise a skill from `currentLevel` to the next (scales up). */
export function skillCost(def: SkillDef, currentLevel: number): number {
  return def.cost * (currentLevel + 1);
}

/** Aggregate gameplay/meta modifiers derived from skill levels. */
export interface MetaEffects {
  /** Multiplier applied to all run score (growth: score_boost). */
  scoreMult: number;
  /** Probability to bump a spawned tier up by one (growth: rich_spawn). */
  spawnBias: number;
  /** Combo-gauge charge gain multiplier (power: charge_flow). */
  chargeGainMult: number;
  /** Charge each run starts with (power: primed). */
  startCharge: number;
  /** Ability cost multiplier, <= 1 (power: efficient). */
  abilityCostMult: number;
  /** Stardust reward multiplier (economy: prosper). */
  stardustMult: number;
  /** XP reward multiplier (economy: scholar). */
  xpMult: number;
}

function lvl(levels: Record<string, number>, id: string): number {
  const def = BY_ID.get(id);
  return Math.min(levels[id] ?? 0, def?.max ?? 0);
}

function per(id: string): number {
  return BY_ID.get(id)?.per ?? 0;
}

/** Summed contribution of a set of skills feeding the same effect channel. */
function sum(levels: Record<string, number>, ids: string[]): number {
  return ids.reduce((acc, id) => acc + per(id) * lvl(levels, id), 0);
}

export function computeEffects(levels: Record<string, number>): MetaEffects {
  return {
    scoreMult: 1 + sum(levels, ["score_boost", "amplify"]),
    spawnBias: Math.min(0.6, per("rich_spawn") * lvl(levels, "rich_spawn")),
    chargeGainMult: 1 + sum(levels, ["charge_flow", "surge"]),
    startCharge: per("primed") * lvl(levels, "primed"),
    abilityCostMult: Math.max(0.5, 1 - per("efficient") * lvl(levels, "efficient")),
    stardustMult: 1 + sum(levels, ["prosper", "fortune"]),
    xpMult: 1 + per("scholar") * lvl(levels, "scholar"),
  };
}

/** Human-facing summary value for a skill at a given level (for UI). */
export function effectLabel(def: SkillDef, level: number): string {
  const v = def.per * level;
  switch (def.id) {
    case "primed":
      return `+${Math.round(v)}`;
    case "efficient":
      return `-${Math.round(v * 100)}%`;
    default:
      return `+${Math.round(v * 100)}%`;
  }
}
